import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { StripeCardElementOptions } from '@stripe/stripe-js';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { calculateItemPrice, SelectionData, PackageData } from './types';

interface CheckoutFormProps {
  totalPrice: number;
  publications: any[];
  isCorpCrunch: boolean;
  selectionData?: SelectionData;
  selectedPackage?: PackageData | null;
  prId?: string | null;
  onConfirm: (success: boolean) => void;
  stripePromise: any;
}

// Persist form data in localStorage
const FORM_STORAGE_KEY = 'checkout_form_data';

const getStoredFormData = () => {
  try {
    const stored = localStorage.getItem(FORM_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // Ignore
  }
  return { billingName: '', billingEmail: '' };
};

const saveFormData = (name: string, email: string) => {
  try {
    localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify({ billingName: name, billingEmail: email }));
  } catch (e) {
    // Ignore
  }
};

const StripePaymentForm: React.FC<{
  totalPrice: number;
  publications: any[];
  isCorpCrunch: boolean;
  selectionData?: SelectionData;
  selectedPackage?: PackageData | null;
  prId?: string | null;
  onConfirm: (success: boolean) => void;
}> = ({ totalPrice, publications, isCorpCrunch, selectionData, selectedPackage, prId, onConfirm }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Load from localStorage and persist - use ref to prevent re-initialization
  const initialData = useRef(getStoredFormData());
  const [billingName, setBillingName] = useState(initialData.current.billingName || '');
  const [billingEmail, setBillingEmail] = useState(initialData.current.billingEmail || '');
  
  // Save to localStorage whenever values change (debounced)
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveFormData(billingName, billingEmail);
    }, 300); // Debounce saves
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [billingName, billingEmail]);

  // Restore from localStorage on mount and when component remounts
  useEffect(() => {
    const stored = getStoredFormData();
    if (stored.billingName && !billingName) {
      setBillingName(stored.billingName);
    }
    if (stored.billingEmail && !billingEmail) {
      setBillingEmail(stored.billingEmail);
    }
  }, []); // Only run on mount

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('Payment system is still loading. Please wait a moment.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Payment field is not ready. Please reload the page and try again.');
      return;
    }

    if (!billingName.trim() || !billingEmail.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const apiUrl = getApiBaseUrl();
      const token = localStorage.getItem('token');

      const response = await fetch(`${apiUrl}/api/payments/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          amount: Math.round(totalPrice * 100), // Convert to cents for Stripe
          channels: publications.map((pub: any) => ({
            id: pub.id,
            name: pub.name,
            price: isCorpCrunch
              ? 1499
              : selectionData
              ? calculateItemPrice(selectionData.packageType, selectionData.tier, 1).pricePerUnit
              : pub.price || 250,
            tier: pub.tier,
          })),
          billingInfo: {
            email: billingEmail,
            firstName: billingName,
            lastName: '',
          },
          currency: 'usd',
          prId: prId || null,
          packageType: selectedPackage?.type || undefined,
          packageTier: selectedPackage?.tier || undefined,
          customPackageTierPRCounts: selectedPackage?.tierPRCounts || undefined,
          packageCredits: selectedPackage?.credits || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to create payment intent' }));
        throw new Error(errorData.error || 'Failed to create payment intent');
      }

      const data = await response.json();
      const clientSecret = data.clientSecret;

      if (!clientSecret) {
        throw new Error('Missing payment information from server. Please try again.');
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: billingName,
            email: billingEmail,
          },
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message || 'Payment failed. Please try again.');
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Immediately update UI so the user is not blocked while we sync with the backend
        setProcessing(false);
        onConfirm(true);

        // Confirm payment with backend to save payment record (fire-and-forget)
        (async () => {
          try {
            const confirmResponse = await fetch(`${apiUrl}/api/payments/confirm-payment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
              },
              body: JSON.stringify({
                paymentIntentId: paymentIntent.id,
                prId: prId || null
              }),
            });

            if (!confirmResponse.ok) {
              const errorData = await confirmResponse.json().catch(() => ({ error: 'Failed to confirm payment' }));
              console.warn('Payment succeeded but confirmation failed:', errorData.error);
            } else {
              const confirmData = await confirmResponse.json();
              console.log('✅ Payment confirmed and saved:', confirmData.paymentId);
            }
            
            // Clear form data after successful payment confirmation
            localStorage.removeItem(FORM_STORAGE_KEY);
          } catch (confirmError) {
            console.error('⚠️  Error confirming payment (non-critical):', confirmError);
          }
        })();
      } else {
        throw new Error('Payment was not completed. Please try again.');
      }
    } catch (err: any) {
      console.error('Stripe payment error:', err);
      setError(err.message || 'Payment processing failed. Please try again.');
      setProcessing(false);
      onConfirm(false);
    }
  };

  const cardElementOptions: StripeCardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#09273b',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#fa755a',
      },
    },
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ mb: 2 }}>
        <TextField
          label="Full Name"
          fullWidth
          required
          size="small"
          value={billingName}
          onChange={(e) => setBillingName(e.target.value)}
          sx={{ mb: 1.5 }}
          disabled={processing}
        />
        <TextField
          label="Email"
          type="email"
          fullWidth
          required
          size="small"
          value={billingEmail}
          onChange={(e) => setBillingEmail(e.target.value)}
          disabled={processing}
        />
      </Box>

      <Box
        sx={{
          p: 2,
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          backgroundColor: '#fafafa',
          mb: 2,
        }}
      >
        <CardElement options={cardElementOptions} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={processing || !billingName.trim() || !billingEmail.trim()}
        endIcon={
          processing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <ArrowForwardIcon />
        }
        sx={{
          backgroundColor: '#ff5200',
          color: '#fff',
          py: 2,
          fontSize: '1.1rem',
          fontWeight: 700,
          borderRadius: 2,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#e64a00',
            boxShadow: 'none',
          },
          '&:disabled': {
            backgroundColor: '#ccc',
          },
        }}
      >
        {processing ? 'Processing...' : `Pay $${totalPrice.toLocaleString()}`}
      </Button>
    </Box>
  );
};

export const CheckoutForm: React.FC<CheckoutFormProps> = ({
  totalPrice,
  publications,
  isCorpCrunch,
  selectionData,
  selectedPackage,
  prId,
  onConfirm,
  stripePromise
}) => {
  if (!stripePromise) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Stripe is not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY environment variable.
      </Alert>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <StripePaymentForm
        totalPrice={totalPrice}
        publications={publications}
        isCorpCrunch={isCorpCrunch}
        selectionData={selectionData}
        selectedPackage={selectedPackage}
        prId={prId}
        onConfirm={onConfirm}
      />
    </Elements>
  );
};

